import type { Offer } from "@/api/hooks/offers"

import {
  ADW_COLUMNS,
  ADW_COLUMN_STORAGE_KEY,
  ADW_VISIBILITY_STORAGE_KEY,
  KLOW_VISIBILITY_STORAGE_KEY,
  ROOTS_VISIBILITY_STORAGE_KEY,
  loadColumnOrder,
  loadColumnVisibility,
} from "../lib/columns"
import {
  computePresetRange,
  type OffersDatePreset,
} from "../lib/presets"

/**
 * Reducer for offers page state: filters, pagination, sort, column order.
 * Parity: `offers/index.html` globals: currentSort, currentSortOrder,
 * currentPage, pageSize, currentStartDate, currentEndDate, statusFilter,
 * selectedUserIds, etc.
 */

export type OffersTab = "roots" | "adw" | "brm" | "klow"

export interface OffersState {
  activeTab: OffersTab
  // Filters
  status: string
  selectedUserIds: number[]
  datePreset: OffersDatePreset
  startDate: string
  endDate: string
  dateDisplayLabel: string
  search: string
  // Pagination (server-side)
  page: number
  pageSize: number
  // Sort
  sortBy: string
  sortOrder: "asc" | "desc"
  // Column order + per-tab visibility
  columnOrder: string[]
  rootsHiddenColumns: Set<string>
  adwHiddenColumns: Set<string>
  klowHiddenColumns: Set<string>
  // Modal
  offerModalOpen: boolean
  editingOffer: EditingOffer | null
  keywordsModalOpen: boolean
  keywordsOfferId: number | null
  keywordsOfferName: string
  klowOfferModalOpen: boolean
  klowEditOffer: Offer | null
}

export interface EditingOffer {
  id: number | null
  /** Present when editing an existing row (from API). */
  public_slug?: string | null
  name: string
  network: string
  target_url: string | null
  partner_domain: string | null
  headline: string | null
  terms: string | null
  rac: string | null
  pixel_db_id: number | null
  event_name: string | null
  vertical_slug: string | null
  /** Display name for `vertical_slug`; optional on non–vertical-backed rows. */
  vertical_name?: string | null
}

export type OffersAction =
  | { type: "SET_TAB"; tab: OffersTab }
  | { type: "SET_STATUS"; value: string }
  | { type: "SET_USER_IDS"; ids: number[] }
  | {
      type: "SET_PRESET"
      preset: OffersDatePreset
      startDate: string
      endDate: string
      displayLabel: string
    }
  | {
      type: "SET_CUSTOM_RANGE"
      startDate: string
      endDate: string
      displayLabel: string
    }
  | { type: "SET_SEARCH"; value: string }
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_SORT"; key: string; columnType: "string" | "number" }
  | { type: "SET_COLUMN_ORDER"; order: string[] }
  | { type: "TOGGLE_COLUMN_VISIBILITY"; tab: OffersTab; columnId: string }
  | { type: "OPEN_CREATE_MODAL" }
  | { type: "OPEN_EDIT_MODAL"; offer: EditingOffer }
  | { type: "CLOSE_OFFER_MODAL" }
  | { type: "OPEN_KEYWORDS_MODAL"; offerId: number; offerName: string }
  | { type: "CLOSE_KEYWORDS_MODAL" }
  | { type: "OPEN_KLOW_CREATE_MODAL" }
  | { type: "CLOSE_KLOW_MODAL" }
  | { type: "OPEN_KLOW_EDIT_MODAL"; offer: Offer }
  | { type: "CLOSE_KLOW_EDIT_MODAL" }
  | { type: "FORCE_STATUS_FILTER"; value: string }

export function createInitialOffersState(): OffersState {
  // Default tab is Roots → NY reporting calendar (same as Ads Manager Roots).
  const today = computePresetRange("today", new Date(), "ny")
  return {
    activeTab: "roots",
    status: "Active",
    selectedUserIds: [],
    datePreset: "today",
    startDate: today.startDate,
    endDate: today.endDate,
    dateDisplayLabel: today.displayLabel,
    search: "",
    page: 1,
    pageSize: 50,
    sortBy: "cost",
    sortOrder: "desc",
    columnOrder: loadColumnOrder(ADW_COLUMN_STORAGE_KEY, ADW_COLUMNS),
    rootsHiddenColumns: loadColumnVisibility(ROOTS_VISIBILITY_STORAGE_KEY),
    adwHiddenColumns: loadColumnVisibility(ADW_VISIBILITY_STORAGE_KEY),
    klowHiddenColumns: loadColumnVisibility(KLOW_VISIBILITY_STORAGE_KEY),
    offerModalOpen: false,
    editingOffer: null,
    keywordsModalOpen: false,
    keywordsOfferId: null,
    keywordsOfferName: "",
    klowOfferModalOpen: false,
    klowEditOffer: null,
  }
}

export function offersReducer(
  state: OffersState,
  action: OffersAction,
): OffersState {
  switch (action.type) {
    case "SET_TAB": {
      if (action.tab === state.activeTab) return state
      const next: OffersState = { ...state, activeTab: action.tab, page: 1 }
      if (state.datePreset === "custom") return next
      const tzMode = action.tab === "roots" ? "ny" : "local"
      const range = computePresetRange(state.datePreset, new Date(), tzMode)
      return {
        ...next,
        startDate: range.startDate,
        endDate: range.endDate,
        dateDisplayLabel: range.displayLabel,
      }
    }

    case "SET_STATUS":
      return { ...state, status: action.value, page: 1 }

    case "FORCE_STATUS_FILTER":
      return { ...state, status: action.value }

    case "SET_USER_IDS":
      return { ...state, selectedUserIds: action.ids, page: 1 }

    case "SET_PRESET":
      return {
        ...state,
        datePreset: action.preset,
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        page: 1,
      }

    case "SET_CUSTOM_RANGE":
      return {
        ...state,
        datePreset: "custom",
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        page: 1,
      }

    case "SET_SEARCH":
      return { ...state, search: action.value }

    case "SET_PAGE":
      return { ...state, page: action.page }

    case "SET_SORT": {
      if (state.sortBy === action.key) {
        return {
          ...state,
          sortOrder: state.sortOrder === "asc" ? "desc" : "asc",
          page: 1,
        }
      }
      return {
        ...state,
        sortBy: action.key,
        sortOrder: action.columnType === "string" ? "asc" : "desc",
        page: 1,
      }
    }

    case "SET_COLUMN_ORDER":
      return { ...state, columnOrder: action.order }

    case "TOGGLE_COLUMN_VISIBILITY": {
      const key = hiddenColumnsKey(action.tab)
      const current = state[key] as Set<string>
      const next = new Set(current)
      if (next.has(action.columnId)) {
        next.delete(action.columnId)
      } else {
        next.add(action.columnId)
      }
      return { ...state, [key]: next }
    }

    case "OPEN_CREATE_MODAL":
      return { ...state, offerModalOpen: true, editingOffer: null }

    case "OPEN_EDIT_MODAL":
      return { ...state, offerModalOpen: true, editingOffer: action.offer }

    case "CLOSE_OFFER_MODAL":
      return { ...state, offerModalOpen: false, editingOffer: null }

    case "OPEN_KEYWORDS_MODAL":
      return {
        ...state,
        keywordsModalOpen: true,
        keywordsOfferId: action.offerId,
        keywordsOfferName: action.offerName,
      }

    case "CLOSE_KEYWORDS_MODAL":
      return {
        ...state,
        keywordsModalOpen: false,
        keywordsOfferId: null,
        keywordsOfferName: "",
      }

    case "OPEN_KLOW_CREATE_MODAL":
      return { ...state, klowOfferModalOpen: true }

    case "CLOSE_KLOW_MODAL":
      return { ...state, klowOfferModalOpen: false }

    case "OPEN_KLOW_EDIT_MODAL":
      return { ...state, klowEditOffer: action.offer }

    case "CLOSE_KLOW_EDIT_MODAL":
      return { ...state, klowEditOffer: null }

    default:
      return state
  }
}

function hiddenColumnsKey(
  tab: OffersTab,
): "rootsHiddenColumns" | "adwHiddenColumns" | "klowHiddenColumns" {
  switch (tab) {
    case "roots":
      return "rootsHiddenColumns"
    case "adw":
      return "adwHiddenColumns"
    case "klow":
      return "klowHiddenColumns"
    default:
      return "rootsHiddenColumns"
  }
}
